import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { getManagerLevel } from "../../api/account/getManagerLevel";
import { getProject } from "../../api/project";
import { ActivityList } from "../../components/Activity";
import { Files } from "../../components/Files";
import { ManagersList } from "../../components/Managers";
import { ProjectDetailForm } from "../../components/Projects";
import { useTranslationHook } from "../../hooks";
import { setLoading } from "../../store/UI";
import { ManagerLevel } from "../../types/model/account";
import { Project } from "../../types/model/project";
import { StoreInterfaces } from "../../types/store";

const DetailsProject = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslationHook();

  const location = useLocation();
  const fromAdd = location.state?.fromAdd;

  const { accountId, projectId } = useParams<{
    accountId: string;
    projectId: string;
  }>();

  const session = useSelector((state: StoreInterfaces) => state.session);

  const [projectData, setProjectData] = useState<Project | null>(null);
  const [managerLevel, setManagerLevel] = useState<ManagerLevel>();
  const [tabIndex, setTabIndex] = useState(0);

  const isAdmin = useSelector( (state: StoreInterfaces) => state.session.isAdministrator, );
  const isTenderUser =
    !!session?.accessToken &&
    !session.isAdministrator &&
    !session.isManager;

  const readOnly = isTenderUser ||
    (!!managerLevel && managerLevel.level !== "account" && managerLevel.level !== "project" );
  
  const fetchManagerLevel = async () => {
    try {
      const data = await getManagerLevel(Number(accountId), Number(projectId));
      setManagerLevel(data);
    } catch (error) {
      console.error(error);
    }
  };
  
  const fetchProject = async () => {
    dispatch(setLoading(true));
    try {
      const data = await getProject(Number(projectId));
      setProjectData(data);
      enqueueSnackbar(t("generic.data-loaded"), { variant: "success" });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchManagerLevel();
    fetchProject();
  }, [projectId]);

  const handleActivitySelection = (activityId: number) => {
    navigate(
      `/accounts/${accountId}/projects/${projectId}/activities/${activityId}`,
    );
  };

  const handleAddActivity = () => {
    navigate(
      `/accounts/${accountId}/projects/${projectId}/activities/add-activity`,
    );
  };

  if (!projectData) return <CircularProgress />;

  const allTabs = [
    {
      id: "details",
      label: t("projects.tabs.detail"),
      hideForAdmin: false,
      component: managerLevel && (
        <ProjectDetailForm
          project={projectData}
          onUpdate={fetchProject}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "activities",
      label: t("projects.tabs.activities"),
      hideForAdmin: true,
      component: managerLevel && (
        <ActivityList
          projectId={Number(projectId)}
          onActivitySelected={handleActivitySelection}
          onAddActivity={handleAddActivity}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "managers",
      label: t("projects.tabs.managers"),
      hideForAdmin: true,
      component: managerLevel && (
        <ManagersList
          accountId={Number(accountId)}
          projectId={Number(projectId)}
          managerLevel={managerLevel}
          isObjectClosedOrCanceled={projectData.closed || projectData.deleted}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "files",
      label: t("generic.files"),
      hideForAdmin: true,
      component: managerLevel && (
        <Files accountId={Number(accountId)} projectId={Number(projectId)} readOnly={readOnly} />
      ),
    },
  ];

  const tabs = allTabs.filter((tab) => !(isAdmin && tab.hideForAdmin));

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("generic.project")} - {projectData.name}
        </Typography>
        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => {
              if (fromAdd) {
                navigate(`/projects/list`);
              } else {
                navigate(-1);
              }
            }}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.id} label={tab.label} />
        ))}
      </Tabs>

      <Box sx={{ p: 3 }}>
        {tabs.map((tab, i) => (
          <Fade
            key={tab.id}
            in={tabIndex === i}
            timeout={{ enter: 200, exit: 0 }}
            unmountOnExit
          >
            <Box>{tab.component}</Box>
          </Fade>
        ))}
      </Box>
    </Paper>
  );
};

export default DetailsProject;
